import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FilterState } from '@app/core/services/container/filter.service';
import { SidenavService } from '@app/core/services/container/sidenav.service';
import { ProfileService } from '@app/core/services/profile/profile.service';
import { HierarchySelectionFilterComponent } from '@app/features/hierarchy/widgets/hierarchy-selection-filter/hierarchy-selection-filter.component';
import { FieldType } from '@app/shared/constants';
import { FieldOption } from '@app/shared/models/field.interface';
import { CustomFilter, FilterOutput } from '@app/shared/models/filter.interface';
import { RSQLOperator } from '@app/shared/models/rsql/rsql.operators';
import { CustomOverlayDescriptor } from '@app/shared/services/overlay/custom-overlay.service';
import { StateService } from '@components/data/data-table-new/state/state.service';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { HierarchySelectionFilterData, UserListOverlayFilterData } from './user-list-overlay-filter.component.data';

const FILTER_OUTPUT_KEYS = {
  active: 'active',
  profile: 'profile.id',
  divisions: 'userDivisions.division.id'
};

@Component({
  selector: 'vfs-user-list-overlay-filter',
  templateUrl: './user-list-overlay-filter.component.html',
  styleUrls: ['./user-list-overlay-filter.component.scss'],
  providers: [UserListOverlayFilterData]
})
export class UserListOverlayFilterComponent implements OnInit {
  @Input() id: string;
  @Input() customOverlayDescriptor: CustomOverlayDescriptor;
  @Input() customFilters: Array<CustomFilter> = [];
  @Input() overlaySubject$: Subject<boolean>;
  @Output() accepted = new EventEmitter<FilterOutput[]>();

  @ViewChild(HierarchySelectionFilterComponent) hieFilter: HierarchySelectionFilterComponent;

  filterState: FilterState;
  filtersForm: FormGroup;
  hierarchySelectionFilterData: HierarchySelectionFilterData = [];
  customFiltersMap = new Map<string, CustomFilter>();
  profiles$: Observable<FieldOption[]>;

  constructor(
    @Inject('StateService') protected stateService: StateService,
    protected formBuilder: FormBuilder,
    protected sidenavService: SidenavService,
    protected profileService: ProfileService,
    public filterData: UserListOverlayFilterData
  ) {
    this.filtersForm = this.formBuilder.group({
      active: [''],
      profile: [''],
      divisions: [[]]
    });
    this.customFilters.forEach((customFilter: CustomFilter) => {
      this.filtersForm.addControl(customFilter.fieldName, customFilter.formControl);
      this.customFiltersMap.set(customFilter.fieldName, customFilter);
    });
  }

  ngOnInit() {
    this.filterState = this.stateService.get(this.id).filter;
    this.profiles$ = this.profileService
      .getActiveProfiles()
      .pipe(map(profiles => profiles.map(profile => ({ label: profile.name, value: profile.id }))));

    if (Object.keys(this.filterState.value).length > 0) {
      this.setStateFilter();
      this.filterData.hierarchySelectionFilterData = this.hierarchySelectionFilterData;
    }
  }

  close() {
    this.sidenavService.closeSidenav();
  }

  apply() {
    this.accepted.emit(this.getFilters());
  }

  clear() {
    this.filtersForm.reset();
    this.filtersForm.patchValue({
      active: '',
      profile: '',
      divisions: []
    });
    this.filterData.clear();
    this.hierarchySelectionFilterData = null;
    this.hieFilter.stringTreeData = [];
  }

  protected getFilters(): FilterOutput[] {
    const filters: FilterOutput[] = [
      this.getStatusFilter(),
      this.getDivisionFilter(),
      this.getProfileFilter(),
      this.getHierarchyFilter()
    ];
    this.customFilters.forEach((customFilter: CustomFilter) => filters.push(customFilter.buildFun()));
    return filters;
  }

  protected getStatusFilter(): FilterOutput {
    return {
      key: 'active',
      value: this.filtersForm.value.active,
      valueType: FieldType.BOOLEAN,
      operator: RSQLOperator.EQUAL
    };
  }

  protected getDivisionFilter(): FilterOutput {
    return {
      key: 'userDivisions.division.id',
      value: this.filtersForm.value.divisions,
      valueType: FieldType.UUID,
      operator: RSQLOperator.IN
    };
  }

  protected getProfileFilter(): FilterOutput {
    return {
      key: 'profile.id',
      value: this.filtersForm.value.profile,
      valueType: FieldType.UUID,
      operator: RSQLOperator.EQUAL
    };
  }

  protected getHierarchyFilter(): FilterOutput {
    return {
      key: 'id',
      value: this.filterData.hierarchySelectionFilterData,
      valueType: FieldType.HIERARCHY,
      operator: RSQLOperator.IN_HIERARCHY
    };
  }

  public hierarchyChange(value: HierarchySelectionFilterData) {
    if (value instanceof Array) {
      this.filterData.hierarchySelectionFilterData = value;
    }
  }

  protected setStateFilter() {
    Object.keys(this.filtersForm.value)
      .filter(key => key !== 'hierarchySelectionControl' && this.filterState.value[FILTER_OUTPUT_KEYS[key]])
      .forEach(key => this.filtersForm.get(key).setValue(this.filterState.value[FILTER_OUTPUT_KEYS[key]].value));

    if (this.filterState.value.id && this.filterState.value.id.value) {
      const hierarchyState = this.filterState.value.id.value;
      this.hierarchySelectionFilterData = [
        hierarchyState[0],
        hierarchyState[1],
        hierarchyState[2],
        hierarchyState[3],
        hierarchyState[4]
      ];
    } else {
      this.hierarchySelectionFilterData = [];
    }
  }
}
